import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Box } from '@withjoy/joykit';
import { ChevronBack, ChevronRight } from '@withjoy/joykit/icons';
import { getSupplementaryImages } from '@apps/card/constants/pdpSupplementaryImages';
import { getSuiteImageUrl, suiteImageKey, SUITE_IMAGE_KEYS } from '@apps/card/utils/suiteImage';
import { useFeatureValue } from '@shared/core/featureFlags';
import { CardThumbnail } from './CardThumbnail';
import type { StationeryTemplate } from '../../../DesignsGallery.types';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';

const SWIPE_THRESHOLD_PX = 30;

type ArrowButtonProps = {
  direction: 'prev' | 'next';
  onClick: (e: React.MouseEvent) => void;
  visible: boolean;
};

const ArrowButton = ({ direction, onClick, visible }: ArrowButtonProps) => (
  <Box
    as="button"
    type="button"
    aria-label={direction === 'prev' ? 'Previous image' : 'Next image'}
    onClick={onClick}
    display="flex"
    justifyContent="center"
    alignItems="center"
    position="absolute"
    top="50%"
    {...(direction === 'prev' ? { left: '8px' } : { right: '8px' })}
    zIndex={4}
    width="28px"
    height="28px"
    borderRadius="50%"
    backgroundColor="white"
    color="mono14"
    boxShadow="0px 4px 12px -2px rgba(44, 41, 37, 0.2), 0px 2px 6px -2px rgba(0,0,0,0.15)"
    style={{
      transform: 'translateY(-50%)',
      opacity: visible ? 1 : 0,
      transition: 'opacity 150ms ease',
      pointerEvents: visible ? 'auto' : 'none',
      border: 'none',
      cursor: 'pointer',
      padding: 0,
      lineHeight: 0,
    }}
  >
    {direction === 'prev' ? (
      <ChevronBack
        size={16}
        title={undefined}
        aria-hidden
      />
    ) : (
      <ChevronRight
        size={16}
        title={undefined}
        aria-hidden
      />
    )}
  </Box>
);

type GalleryTileCarouselProps = {
  template: StationeryTemplate;
  selectedColorwayId?: string;
  fallbackImageUrl?: string;
  /** Describes the design on the live preview. Slides stay `alt=""`: they mount only after interaction (never in crawled HTML) and would repeat it for screen readers. */
  altText: string;
  children?: ReactNode;
};

export const GalleryTileCarousel = ({ template, selectedColorwayId, fallbackImageUrl, altText, children }: GalleryTileCarouselProps) => {
  const isMobile = useIsMobileScreen();
  const { value: suiteImageFlag } = useFeatureValue('printPdpSuiteImage');
  const vendor = template.format === 'digital' ? 'joy_digital' : 'taylor';
  // Per-design suite flat-lay goes first among supplementary images so it
  // shows as the second slide, right after the live card preview.
  const suiteKey = template.themeId ? suiteImageKey(template.themeId, selectedColorwayId) : undefined;
  const suiteImageSrc = suiteKey && SUITE_IMAGE_KEYS.has(suiteKey) ? getSuiteImageUrl(suiteKey) : undefined;
  const supplementaryImages = [
    ...(suiteImageFlag === 'on' && suiteImageSrc ? [suiteImageSrc] : []),
    ...getSupplementaryImages(
      {
        category: template.category,
        widthInches: template.widthInches,
        heightInches: template.heightInches,
        // StD templates also sell as magnets on the same 4x6 size; a template that
        // has no plain paper product is magnet-only and must not take the postcard branch.
        productType: template.products.some(p => p.productType === 'stationeryCard') ? undefined : 'magnet',
      },
      vendor,
    ),
  ];
  const totalSlides = 1 + supplementaryImages.length;
  // The static fallback is boxed to the template's own size, so a landscape thumbnail isn't cropped.
  const fallbackCardSize = { widthInches: template.widthInches, heightInches: template.heightInches };

  const [currentIndex, setCurrentIndex] = useState(0);
  // Supplementary images are only mounted after the first interaction (hover/touch/arrow tap) — lazy load.
  const [slidesLoaded, setSlidesLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  // Disabled during the snap-back-to-0 reset on mouse leave to avoid a backwards animation.
  const [animating, setAnimating] = useState(true);

  const rafRef = useRef<number>();
  const touchStartX = useRef(0);

  useEffect(
    () => () => {
      if (rafRef.current !== undefined) cancelAnimationFrame(rafRef.current);
    },
    [],
  );

  const handleMouseEnter = useCallback(() => {
    setSlidesLoaded(true);
    setIsHovered(true);
    setAnimating(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    // Snap back to the card thumbnail without animation, then re-enable transitions.
    setAnimating(false);
    setCurrentIndex(0);
    if (rafRef.current !== undefined) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = requestAnimationFrame(() => setAnimating(true));
    });
  }, []);

  const navigate = useCallback(
    (dir: 1 | -1) => {
      setSlidesLoaded(true);
      setAnimating(true);
      setCurrentIndex(i => (dir > 0 ? Math.min(i + 1, totalSlides - 1) : Math.max(i - 1, 0)));
    },
    [totalSlides],
  );

  const handlePrev = useCallback(
    (e: React.MouseEvent) => {
      // The tile may be an <a>; stopPropagation alone doesn't stop the link's navigation.
      e.preventDefault();
      e.stopPropagation();
      navigate(-1);
    },
    [navigate],
  );

  const handleNext = useCallback(
    (e: React.MouseEvent) => {
      // The tile may be an <a>; stopPropagation alone doesn't stop the link's navigation.
      e.preventDefault();
      e.stopPropagation();
      navigate(1);
    },
    [navigate],
  );

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setSlidesLoaded(true);
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      const delta = touchStartX.current - e.changedTouches[0].clientX;
      if (Math.abs(delta) < SWIPE_THRESHOLD_PX) return;
      setAnimating(true);
      setCurrentIndex(i => (delta > 0 ? Math.min(i + 1, totalSlides - 1) : Math.max(i - 1, 0)));
    },
    [totalSlides],
  );

  // No supplementary images — render the thumbnail as-is (no carousel overhead).
  if (totalSlides <= 1) {
    return (
      <CardThumbnail
        template={template}
        selectedColorwayId={selectedColorwayId}
        fallbackImageUrl={fallbackImageUrl}
        fallbackCardSize={fallbackCardSize}
        fallbackFitsLivePreview
        altText={altText}
      >
        {children}
      </CardThumbnail>
    );
  }

  // Arrows: always visible on mobile (no hover state); appear on hover on desktop.
  const arrowsVisible = isMobile || isHovered;

  return (
    <Box
      position="relative"
      overflow="hidden"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Slide 0: live card preview — stays in place, supplementary images slide over it */}
      <CardThumbnail
        template={template}
        selectedColorwayId={selectedColorwayId}
        fallbackImageUrl={fallbackImageUrl}
        fallbackCardSize={fallbackCardSize}
        fallbackFitsLivePreview
        altText={altText}
      />

      {/* Slides 1+: PDP supplementary images. Mounted lazily on first interaction. */}
      {slidesLoaded &&
        supplementaryImages.map((src, i) => (
          <Box
            key={src}
            as="img"
            src={src}
            alt=""
            loading="lazy"
            position="absolute"
            top={0}
            left={0}
            width="100%"
            height="100%"
            zIndex={2}
            style={{
              objectFit: 'cover',
              transform: `translateX(${(i + 1 - currentIndex) * 100}%)`,
              transition: animating ? 'transform 300ms ease' : 'none',
            }}
          />
        ))}

      {/* Badges rendered above supplementary images */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        zIndex={3}
      >
        {children}
      </Box>

      <ArrowButton
        direction="prev"
        onClick={handlePrev}
        visible={arrowsVisible && currentIndex > 0}
      />
      <ArrowButton
        direction="next"
        onClick={handleNext}
        visible={arrowsVisible && currentIndex < totalSlides - 1}
      />
    </Box>
  );
};
